import { useMemo, useState } from "react";
import { useTerm } from "@/lib/terminology.jsx";
import { CheckSquare, Square } from "lucide-react";

/**
 * v5.13-r2 DealChoicePopover — shown when the user clicks Complete or Snooze
 * on a task that is linked to >=2 open deals from the standalone Tasks page
 * (or Task Detail slide-over). Never rendered from inside a specific deal's
 * Tasks tab.
 *
 * Props:
 *  - onClose(): void
 *  - onConfirm(dealIds: string[]): void   // called with the checked deal_ids
 *  - open: boolean
 *  - title: string
 *  - description?: string
 *  - actionLabel: string                  // e.g. "Complete" | "Confirm snooze"
 *  - openLinkedDeals: [{deal_id, company_name}]  // ONLY currently-open links
 *  - accent?: "primary" | "danger"        // color of confirm button
 *  - extra?: ReactNode                    // e.g. a snooze-date input rendered inside
 *  - testid?: string
 */
export default function DealChoicePopover({
  onClose,
  onConfirm,
  open,
  title,
  description,
  actionLabel,
  openLinkedDeals,
  accent = "primary",
  extra = null,
  testid = "deal-choice-popover",
}) {
  const term = useTerm();
  const [selected, setSelected] = useState(() =>
    openLinkedDeals.length === 1 ? [openLinkedDeals[0].deal_id] : []
  );
  if (!open) return null;

  const allChecked = selected.length === openLinkedDeals.length;
  const toggle = (id) => {
    setSelected(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id]
    );
  };
  const toggleAll = () => {
    setSelected(allChecked ? [] : openLinkedDeals.map((d) => d.deal_id));
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="cw-h2 text-base">{title}</h3>
        {description && (
          <div className="text-xs text-[var(--cw-muted)] mt-1">{description}</div>
        )}

        <div className="mt-3">
          <div
            className="text-xs cw-caption uppercase tracking-widest mb-2"
          >
            Choose {term("deals").toLowerCase()}
          </div>
          <button
            type="button"
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm w-full px-2 py-1.5 rounded hover:bg-[rgba(40,42,116,0.05)]"
            data-testid={`${testid}-select-all`}
          >
            {allChecked ? (
              <CheckSquare size={14} className="text-[var(--cw-primary)]" />
            ) : (
              <Square size={14} className="text-[var(--cw-muted)]" />
            )}
            <span className="font-semibold">
              Select all ({openLinkedDeals.length})
            </span>
          </button>

          <div
            className="mt-1 max-h-64 overflow-auto rounded border"
            style={{ borderColor: "var(--cw-border)" }}
          >
            {openLinkedDeals.map((d) => {
              const isSel = selected.includes(d.deal_id);
              return (
                <button
                  key={d.deal_id}
                  type="button"
                  onClick={() => toggle(d.deal_id)}
                  className="flex items-center gap-2 text-sm w-full px-3 py-1.5 hover:bg-[rgba(40,42,116,0.05)]"
                  data-testid={`${testid}-check-${d.deal_id}`}
                >
                  {isSel ? (
                    <CheckSquare size={14} className="text-[var(--cw-primary)]" />
                  ) : (
                    <Square size={14} className="text-[var(--cw-muted)]" />
                  )}
                  <span>{d.company_name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {extra}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="cw-btn-ghost text-sm"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            disabled={selected.length === 0}
            onClick={() => onConfirm(selected)}
            className={`text-sm px-3 py-1.5 rounded ${
              selected.length === 0 ? "opacity-40 cursor-not-allowed" : ""
            }`}
            style={
              accent === "danger"
                ? { background: "var(--cw-error)", color: "white" }
                : { background: "var(--cw-primary)", color: "white" }
            }
            data-testid={`${testid}-confirm`}
          >
            {actionLabel} ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );
}
